"use client"

import { CheckIcon, CopyIcon, PaletteIcon, RotateCcwIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import type { ResolvedThemeTokens } from "@workspace/theming"
import { renderThemeCss } from "@workspace/theming"

/** Tenant-changeable color tokens (mirrors packages/theming COLOR_TOKEN_KEYS). */
const COLOR_KEYS = [
  "primary",
  "background",
  "foreground",
  "accent",
  "muted",
] as const

/** The preloaded platform fonts a tenant's `fontFamily` token can select. */
const FONT_OPTIONS = [
  { value: "var(--font-sans-a)", label: "Inter (a)" },
  { value: "var(--font-sans-b)", label: "Poppins (b)" },
  { value: "var(--font-sans-c)", label: "Vazirmatn (c)" },
  { value: "var(--font-sans-d)", label: "Oswald (d)" },
]

const STYLE_TAG_ID = "dev-theme-override"

/**
 * Convert any CSS color the browser can render (oklch, hex, named…) to #rrggbb
 * for the `<input type="color">` swatch. Canvas parses the color; an invalid
 * value leaves the canvas transparent-black, which is a harmless swatch state.
 */
function toHex(value: string): string {
  const canvas = document.createElement("canvas")
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext("2d")
  if (!ctx) return "#000000"
  ctx.fillStyle = value
  ctx.fillRect(0, 0, 1, 1)
  const [r = 0, g = 0, b = 0] = ctx.getImageData(0, 0, 1, 1).data
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`
}

function parseRem(value: string): number | null {
  const match = /^(\d*\.?\d+)rem$/.exec(value.trim())
  return match ? Number(match[1]) : null
}

/**
 * Dev-only live theme editor (rendered inside the dev toolbar). Edits the SAME
 * token set a store can change through its config (`ThemeTokens`, US-012) and
 * previews the result by re-running `renderThemeCss` client-side into a
 * `<style id="dev-theme-override">`. The server-rendered tenant `<style>` sits
 * in <body> (after <head>), so document order can't be relied on; each selector
 * gets an extra `:root` (`html:root` → `html:root:root`) to out-specify it.
 * All the derived vars (dark-mode brand adaptation, brand-tinted dark surfaces,
 * contrast-derived `--accent-foreground`) regenerate exactly as they would for
 * a real config change. Press `d` to flip light/dark while tweaking.
 *
 * Overrides are in-memory only (gone on reload / tenant switch) — "copy JSON"
 * exports the token object for pasting into a fixture tenant's `theme`.
 */
export function DevThemeEditor({
  initialTheme,
}: {
  initialTheme: ResolvedThemeTokens
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [theme, setTheme] = useState<ResolvedThemeTokens>(initialTheme)
  const [copied, setCopied] = useState(false)

  const dirty = useMemo(
    () =>
      theme.radius !== initialTheme.radius ||
      theme.fontFamily !== initialTheme.fontFamily ||
      COLOR_KEYS.some((key) => theme[key] !== initialTheme[key]) ||
      JSON.stringify(theme.dark) !== JSON.stringify(initialTheme.dark),
    [theme, initialTheme]
  )

  useEffect(() => {
    if (!dirty) {
      document.getElementById(STYLE_TAG_ID)?.remove()
      return
    }
    let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null
    if (!tag) {
      tag = document.createElement("style")
      tag.id = STYLE_TAG_ID
      document.head.appendChild(tag)
    }
    tag.textContent = renderThemeCss(theme).replaceAll(
      "html:root",
      "html:root:root"
    )
  }, [theme, dirty])

  // Drop the override if the editor unmounts (e.g. toolbar closed).
  useEffect(() => () => document.getElementById(STYLE_TAG_ID)?.remove(), [])

  function set<K extends keyof ResolvedThemeTokens>(
    key: K,
    value: ResolvedThemeTokens[K]
  ) {
    setTheme((prev) => ({ ...prev, [key]: value }))
  }

  /** Set/clear an explicit dark override; empty value falls back to derived. */
  function setDark(key: (typeof COLOR_KEYS)[number], value: string) {
    setTheme((prev) => {
      const dark = { ...prev.dark }
      if (value.trim() === "") delete dark[key]
      else dark[key] = value
      return { ...prev, dark }
    })
  }

  async function copyJson() {
    // Omit `dark` when empty so a derived-only theme copies clean.
    const { dark, ...rest } = theme
    const exported = Object.keys(dark).length > 0 ? { ...rest, dark } : rest
    await navigator.clipboard.writeText(JSON.stringify(exported, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const radiusRem = parseRem(theme.radius)
  const fontIsKnown = FONT_OPTIONS.some((f) => f.value === theme.fontFamily)

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className={`flex items-center gap-1.5 rounded border border-neutral-600 px-2 py-0.5 hover:bg-neutral-800 ${
          isOpen ? "bg-neutral-800" : ""
        }`}
      >
        <PaletteIcon className="size-3.5" />
        theme
        {dirty ? <span className="size-1.5 rounded-full bg-amber-500" /> : null}
      </button>

      {isOpen ? (
        <div
          dir="ltr"
          className="fixed right-3 bottom-12 z-[9999] w-80 space-y-2 rounded-lg border border-neutral-700 bg-neutral-900 p-3 font-mono text-xs text-neutral-100 shadow-xl"
        >
          <div className="flex items-center justify-between">
            <span className="font-bold">theme tokens</span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                title="Copy theme JSON (paste into fixture tenant)"
                onClick={copyJson}
                className="flex items-center gap-1 rounded border border-neutral-600 px-1.5 py-0.5 hover:bg-neutral-800"
              >
                {copied ? (
                  <CheckIcon className="size-3 text-emerald-400" />
                ) : (
                  <CopyIcon className="size-3" />
                )}
                json
              </button>
              <button
                type="button"
                title="Reset to tenant theme"
                disabled={!dirty}
                onClick={() => setTheme(initialTheme)}
                className="flex items-center gap-1 rounded border border-neutral-600 px-1.5 py-0.5 hover:bg-neutral-800 disabled:opacity-40"
              >
                <RotateCcwIcon className="size-3" />
                reset
              </button>
            </div>
          </div>

          <div className="text-[10px] font-bold tracking-wide text-neutral-500 uppercase">
            light
          </div>
          {COLOR_KEYS.map((key) => (
            <label key={key} className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-neutral-400">{key}</span>
              <input
                type="color"
                aria-label={`${key} color picker`}
                value={toHex(theme[key])}
                onChange={(e) => set(key, e.target.value)}
                className="size-6 shrink-0 cursor-pointer rounded border border-neutral-600 bg-neutral-800 p-0"
              />
              <input
                type="text"
                aria-label={`${key} color value`}
                value={theme[key]}
                onChange={(e) => set(key, e.target.value)}
                spellCheck={false}
                className="w-full rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5"
              />
            </label>
          ))}

          <div className="flex items-center justify-between text-[10px] font-bold tracking-wide text-neutral-500 uppercase">
            <span>dark overrides</span>
            <span className="font-normal text-neutral-600 normal-case">
              empty = derived
            </span>
          </div>
          {COLOR_KEYS.map((key) => {
            const value = theme.dark[key] ?? ""
            return (
              <label key={`dark-${key}`} className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-neutral-400">{key}</span>
                <input
                  type="color"
                  aria-label={`${key} dark color picker`}
                  value={toHex(value || theme[key])}
                  onChange={(e) => setDark(key, e.target.value)}
                  className={`size-6 shrink-0 cursor-pointer rounded border border-neutral-600 bg-neutral-800 p-0 ${
                    value ? "" : "opacity-50"
                  }`}
                />
                <input
                  type="text"
                  aria-label={`${key} dark color value`}
                  placeholder="derived"
                  value={value}
                  onChange={(e) => setDark(key, e.target.value)}
                  spellCheck={false}
                  className="w-full rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5 placeholder:text-neutral-600"
                />
                <button
                  type="button"
                  aria-label={`clear ${key} dark override`}
                  title="Reset to derived"
                  disabled={!value}
                  onClick={() => setDark(key, "")}
                  className="shrink-0 rounded border border-neutral-600 px-1 py-0.5 hover:bg-neutral-800 disabled:opacity-30"
                >
                  <RotateCcwIcon className="size-3" />
                </button>
              </label>
            )
          })}

          <label className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-neutral-400">radius</span>
            <input
              type="range"
              aria-label="radius slider"
              min={0}
              max={2}
              step={0.05}
              value={radiusRem ?? 0}
              disabled={radiusRem === null}
              onChange={(e) => set("radius", `${e.target.value}rem`)}
              className="w-full accent-amber-500"
            />
            <input
              type="text"
              aria-label="radius value"
              value={theme.radius}
              onChange={(e) => set("radius", e.target.value)}
              spellCheck={false}
              className="w-20 shrink-0 rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5"
            />
          </label>

          <label className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-neutral-400">fontFamily</span>
            <select
              aria-label="font family"
              value={theme.fontFamily}
              onChange={(e) => set("fontFamily", e.target.value)}
              className="w-full rounded border border-neutral-600 bg-neutral-800 px-1.5 py-0.5"
            >
              {fontIsKnown ? null : (
                <option value={theme.fontFamily}>{theme.fontFamily}</option>
              )}
              {FONT_OPTIONS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>

          <p className="text-[10px] leading-snug text-neutral-500">
            live preview only — press <kbd>d</kbd> to toggle dark mode; copy
            json into the fixture&apos;s theme to persist
          </p>
        </div>
      ) : null}
    </>
  )
}
